import React from 'react';
import {
  ChevronRight,
  FileText,
  FlaskConical,
  Upload,
  Rocket,
  BarChart3,
  Bot,
  Phone,
  MessagesSquare,
  MessageSquare,
  Mail,
  Users,
} from 'lucide-react';

/* =========================================================
   Shared getting-started guide steps (fresh-road new UI).
   Consumed by DemoTutorialModal (modal walkthrough) and
   DemoCreateAI (full-page "Create New AI" guide).
   ========================================================= */

const Bar = ({ w = 'w-full', c = 'bg-slate-100' }) => <span className={`block h-2 rounded-full ${c} ${w}`} />;

const MockShell = ({ children }) => (
  <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
    {children}
  </div>
);

/* ---- per-step mockups ---- */

const BuildMock = () => (
  <MockShell>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.6fr_1fr]">
      <div className="rounded-xl border border-slate-200 bg-white p-3.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><FileText size={15} /></span>
          <span className="text-[12.5px] font-semibold">System Prompt</span>
        </div>
        <div className="mt-3 space-y-2">
          <Bar w="w-11/12" /><Bar w="w-full" /><Bar w="w-4/5" /><Bar w="w-10/12" /><Bar w="w-2/3" />
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3.5">
        <span className="text-[12.5px] font-semibold">Agent Settings</span>
        <div className="mt-3 space-y-2.5">
          {['Model', 'Voice', 'Tone'].map((l) => (
            <div key={l}>
              <p className="text-[10.5px] font-medium text-slate-400">{l}</p>
              <div className="mt-1 flex items-center justify-between rounded-lg border border-slate-200 px-2 py-1.5">
                <Bar w="w-2/3" c="bg-teal-100" />
                <ChevronRight size={12} className="text-slate-300" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </MockShell>
);

const TestMock = () => (
  <MockShell>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-teal-600 px-3 py-2 text-white">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20"><Bot size={13} /></span>
          <span className="text-[11.5px] font-semibold">Test chat</span>
        </div>
        <div className="space-y-2 p-3">
          <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-slate-100 px-2.5 py-1.5"><Bar w="w-24" /></div>
          <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-sm bg-teal-600 px-2.5 py-1.5"><Bar w="w-20" c="bg-white/70" /></div>
          <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-slate-100 px-2.5 py-1.5"><Bar w="w-28" /></div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-600"><Phone size={15} /></span>
          <span className="text-[12.5px] font-semibold">Test voice call</span>
        </div>
        <div className="mt-3 space-y-2">
          <div className="rounded-lg border border-slate-200 px-2 py-2"><Bar w="w-1/2" /></div>
          <div className="flex items-center justify-center gap-1.5 rounded-lg bg-teal-600 py-2 text-white">
            <Phone size={12} /> <Bar w="w-16" c="bg-white/70" />
          </div>
        </div>
      </div>
    </div>
  </MockShell>
);

const KnowledgeMock = () => (
  <MockShell>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-white p-5 text-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><Upload size={17} /></span>
        <p className="mt-2 text-[11.5px] font-semibold">Add Knowledge</p>
        <p className="text-[10.5px] text-slate-400">PDF, DOCX, TXT…</p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-600"><Users size={15} /></span>
          <span className="text-[12.5px] font-semibold">Customers</span>
        </div>
        <div className="mt-2.5 divide-y divide-slate-100">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center justify-between py-1.5">
              <Bar w="w-24" /><Bar w="w-10" c="bg-slate-100" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </MockShell>
);

const LiveMock = () => (
  <MockShell>
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
      {[
        { name: 'Voice', icon: Phone, tint: 'bg-teal-50 text-teal-600', on: true },
        { name: 'Web Chat', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600', on: true },
        { name: 'SMS', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600', on: false },
        { name: 'Email', icon: Mail, tint: 'bg-orange-50 text-orange-500', on: false },
      ].map(({ name, icon: Icon, tint, on }) => (
        <div key={name} className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between">
            <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tint}`}><Icon size={14} /></span>
            <span className={`h-4 w-7 rounded-full ${on ? 'bg-teal-500' : 'bg-slate-200'}`}>
              <span className={`block h-3 w-3 translate-y-0.5 rounded-full bg-white shadow ${on ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
            </span>
          </div>
          <p className="mt-2 text-[11.5px] font-semibold">{name}</p>
        </div>
      ))}
    </div>
  </MockShell>
);

const AnalyzeMock = () => (
  <MockShell>
    <div className="grid grid-cols-3 gap-2.5">
      {[['Conversations', 'w-16'], ['Resolution', 'w-12'], ['Avg time', 'w-14']].map(([l, w]) => (
        <div key={l} className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-[10.5px] text-slate-400">{l}</p>
          <Bar w={w} c="bg-slate-800" />
        </div>
      ))}
    </div>
    <div className="mt-2.5 rounded-xl border border-slate-200 bg-white p-3">
      <svg viewBox="0 0 240 44" className="w-full">
        <polyline
          points="0,36 40,28 80,30 120,18 160,22 200,10 240,6"
          fill="none"
          stroke="#0D9488"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </MockShell>
);

export const GUIDE_STEPS = [
  {
    title: 'Build your AI',
    icon: FileText,
    body:
      'Go to Customer AI → Script Building. Create a script — a text prompt or a workflow — and shape the agent (model, voice, tone) in the settings panel beside the editor. Everything auto-saves.',
    mockup: <BuildMock />,
    actions: [{ label: 'Open Script Building', route: '/dashboard/customer-ai/scripts' }],
  },
  {
    title: 'Test it',
    icon: FlaskConical,
    body:
      'Open Customer AI → Testing before going live. Chat with your agent as a web visitor, or place a real voice call and watch the live transcript. Tweak the script and test again until it feels right.',
    mockup: <TestMock />,
    actions: [{ label: 'Open Testing', route: '/dashboard/customer-ai/testing' }],
  },
  {
    title: 'Add knowledge & customers',
    icon: Upload,
    body:
      'Upload documents in Customer AI → Knowledge so your AI can ground its answers in your content. Manage customer groups and contact records — or import a CSV — in Customers → CRM.',
    mockup: <KnowledgeMock />,
    actions: [
      { label: 'Open Knowledge', route: '/dashboard/customer-ai/knowledge' },
      { label: 'Open CRM', route: '/dashboard/customers/crm' },
    ],
  },
  {
    title: 'Go live',
    icon: Rocket,
    body:
      'Assign phone numbers in Customer AI → Phone Numbers, then enable the channels your AI should answer on — Voice and Web Chat are ready today. Your agent goes live across every enabled channel.',
    mockup: <LiveMock />,
    actions: [
      { label: 'Phone Numbers', route: '/dashboard/customer-ai/phone-numbers' },
      { label: 'Testing', route: '/dashboard/customer-ai/testing' },
    ],
  },
  {
    title: 'Analyze & improve',
    icon: BarChart3,
    body:
      'Under Activity you have it all: Logs holds every transcript, Analytics shows performance at a glance, Analysis Reports summarizes outcomes and surveys, and Analytics Setup defines what gets measured per script.',
    mockup: <AnalyzeMock />,
    actions: [
      { label: 'Logs', route: '/dashboard/logs' },
      { label: 'Analysis Reports', route: '/dashboard/analysis-reports' },
      { label: 'Analytics Setup', route: '/dashboard/analytics-setup' },
    ],
  },
];

export default GUIDE_STEPS;
